import { AlertTriangle, ArrowRight, Check, ChevronDown } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { ClipboardBlockItem } from '@/lib/clipboard';
import type { ConflictChoice, VariableConflict } from '@/lib/paste-blocks';
import type { Block, TaskVariable } from '@/types/experience';
import type { TFunction } from 'i18next';

import ModalShell from '@/components/modal-shell';
import { BLOCK_META } from '@/components/task/block-library';
import { describeDefinition } from '@/components/task/variable-utils';
import { Button } from '@/components/ui/button';
import { CheckboxRow } from '@/components/ui/checkbox-row';
import { RadioGroup } from '@/components/ui/radio-group';
import { findVariableConflicts, incomingVariables } from '@/lib/paste-blocks';

/**
 * The one question a paste can't answer by itself.
 *
 * Shown only when a copied block carries a variable whose NAME this task already uses for a
 * DIFFERENT definition. Everything else is decided silently: an unknown name is imported, and an
 * identical definition is reused, because in neither case is there anything a teacher could
 * usefully choose between.
 *
 * Each option is written as a TRANSFORMATION, because each changes a different thing and the
 * change is the whole question:
 *
 *     Update Variable   1 to 400, step 1  →  1 to 10, step 0.1
 *     Keep Variable     depth  →  depth_2
 *
 * Side-by-side columns headed "this task" and "copied block" showed the same facts and stated no
 * relationship between them, leaving the teacher to work out which way round the swap went. An
 * arrow says it. Neither option is styled as the recommended one: which is right depends on what
 * the block is for, and a default nudge would be guessing on their behalf.
 */

/** `depth` → `depth_2`, matching what the import will actually name it. */
function previewName(base: string, taken: Set<string>): string {
  if (!taken.has(base)) return base;
  let n = 2;
  while (taken.has(`${base}_${n}`)) n++;
  return `${base}_${n}`;
}

/** A block's own name, falling back to its type label the way every other list in the editor
 *  does. `BLOCK_META` is the one place a type's label lives, so there is nothing to keep in step. */
function nameOf(block: Block, t: TFunction): string {
  if (block.name) return block.name;
  const key = BLOCK_META[block.type]?.labelKey;
  return key ? t(key) : block.type;
}

/** A plain summary/content pair. Native disclosure semantics, so the keyboard and screen reader
 *  behaviour is the platform's rather than something to re-implement and get subtly wrong. */
function Disclosure({ open, onToggle, label, children, tone }: {
  open: boolean; onToggle: () => void; label: string; children: ReactNode;
  /** `warning` for a row whose content can overwrite something. */
  tone?: 'warning';
}) {
  return (
    <div className={`rounded-xl border ${tone === 'warning' ? 'border-ws-warning-fg/40' : 'border-ws-border'}`}>
      <button
        type='button'
        onClick={onToggle}
        aria-expanded={open}
        className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm font-medium ${tone === 'warning' ? 'text-ws-warning-fg' : 'text-ws-text'}`}
      >
        {/* Icon and label travel together — the row is `justify-between`, so loose children
            would scatter the icon left, the label centre and the chevron right. */}
        <span className='flex items-center gap-1.5'>
          {tone === 'warning' && <AlertTriangle aria-hidden='true' className='h-4 w-4 shrink-0' />}
          {label}
        </span>
        <ChevronDown aria-hidden='true' className={`h-4 w-4 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className='border-t border-ws-border p-3'>{children}</div>}
    </div>
  );
}

export default function PasteConflictDialog({ items, subjectId, inForce, onCancel, onConfirm }: {
  /** Everything the clipboard offers, in the SOURCE TASK's order — which is the order the
   *  checklist lists them in and the order they will be pasted in. */
  items: ClipboardBlockItem[];
  /** The block Copy was pressed on. An identity, because `items` is ordered by the source task
   *  and the subject is not reliably first. */
  subjectId?: string;
  /** Every variable resolvable in the destination: what a name clashes with, and what is taken. */
  inForce: TaskVariable[];
  onCancel: () => void;
  onConfirm: (choices: Record<string, ConflictChoice>, include: Set<string>) => void;
}) {
  const { t } = useTranslation();

  const subject = (items.find(i => i.block.id === subjectId) ?? items[0]!).block;
  const subjectFollows = (subject.data as { followsBlockId?: string } | undefined)?.followsBlockId;
  /* The subject either follows the leader or IS it — a set has no third kind of member. */
  const leaderId = subjectFollows ?? subject.id;
  const hasSet = items.length > 1;

  /* The subject is always brought: it is the block the teacher pressed Copy on, and a paste that
     leaves it out is not the paste they asked for. Its row is shown and disabled rather than
     hidden, so the list reads as the whole set rather than as a list with a gap. */
  const [include, setInclude] = useState<Set<string>>(() => new Set(items.map(i => i.block.id)));
  const [showConflicts, setShowConflicts] = useState(false);

  /*
   * CONFLICTS ARE DERIVED FROM THE SELECTION, not passed in — unticking a member can remove a
   * clash entirely, so a list computed before the choice would offer decisions about variables
   * that are no longer coming. This is the whole reason the two questions share one dialog.
   */
  const chosen = items.filter(i => include.has(i.block.id));
  const conflicts = findVariableConflicts(incomingVariables(chosen), inForce);

  // Defaults to `rename` for every conflict, matching what a paste does with no choices at all:
  // keeping the block's own numbers cannot silently change what the teacher already authored.
  // Keyed by variable id and merged, so a choice survives a member being unticked and reticked.
  const [picked, setPicked] = useState<Record<string, ConflictChoice>>({});
  const choices: Record<string, ConflictChoice> = Object.fromEntries(
    conflicts.map(c => [c.incoming.id, picked[c.incoming.id] ?? 'rename' as ConflictChoice]),
  );

  function toggleMember(id: string) {
    setInclude(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  // Renamed previews are computed against a growing set, so two conflicts resolved the same way
  // show the two names they will actually get rather than both claiming `depth_2`.
  const taken = new Set(inForce.map(v => v.name));
  const renameTo = new Map<string, string>();
  for (const c of conflicts) {
    const name = previewName(c.incoming.name, taken);
    taken.add(name);
    renameTo.set(c.incoming.id, name);
  }

  /** One conflict's card. Named because it is rendered from two places — inline when the dialog
   *  is only about names, and inside the disclosure when a selection sits above it. */
  function renderConflict(c: VariableConflict) {
    const newName = renameTo.get(c.incoming.id)!;
        // Each option names what it changes and shows it changing. `remap` re-points the block at
        // this task's variable, so its NUMBERS move; `rename` brings the block's own along, so its
        // NAME moves. One group per variable — a block can carry several and they need not go the
        // same way.
        const transform = (label: string, from: string, to: string) => (
          <span className='flex flex-col gap-0.5'>
            <span className='text-sm font-medium text-ws-text'>{label}</span>
            {/* Wraps rather than truncates: on a narrow phone the halves stack, still in order.
                The arrow is decorative — "becomes" is what a listener gets in its place. */}
            {/* Room either side of the arrow: before and after are read as a pair and the
                comparison is the point, so they should not be crowded into one another. */}
            <span className='flex flex-wrap items-center gap-x-4 text-xs text-ws-muted'>
              <span className='font-mono'>{from}</span>
              <ArrowRight aria-hidden='true' className='h-3.5 w-3.5 shrink-0' />
              <span className='sr-only'>{t('task.paste.conflict.becomes')}</span>
              <span className='font-mono text-ws-text'>{to}</span>
            </span>
          </span>
        );
        return (
          <div key={c.incoming.id} className='flex flex-col gap-2 rounded-xl border border-ws-border p-3'>
            <span id={`paste-conflict-${c.incoming.id}-name`} className='font-mono text-sm font-semibold text-ws-text'>
              {c.incoming.name}
            </span>
            {/* The app's own radio, not the browser's — same dot, same focus behaviour, and the
                arrow keys move between options as they do everywhere else. */}
            <RadioGroup
              aria-label={c.incoming.name}
              value={choices[c.incoming.id] ?? 'rename'}
              onChange={(next) => setPicked(prev => ({ ...prev, [c.incoming.id]: next }))}
              options={[
                { value: 'remap', label: transform(t('task.paste.conflict.update'), describeDefinition(c.incoming, t), describeDefinition(c.existing, t)) },
                { value: 'rename', label: transform(t('task.paste.conflict.keep'), c.incoming.name, newName) },
              ]}
            />
          </div>
        );
  }

  return (
    // Full-bleed on a phone, like the block editors: this list grows with the task, and a dozen
    // rows in a centred card on a 375px-tall landscape screen is a card with no room to be one.
    <ModalShell
      /*
       * THE CONFLICT HEADING IS A WARNING, because the choice under it can overwrite something.
       * Owner: "you're about to overwrite something and the text doesn't catch your attention."
       * Plain heading weight read as a label for a form rather than as a thing to stop at.
       *
       * Only when a clash is actually on screen. "Paste Linked Blocks" is a question about what
       * to bring, not a warning, and colouring every paste heading would spend the signal on the
       * common case — see `feedback_short_warning_and_error_text`.
       */
      title={hasSet ? t('task.paste.bring.title') : (
        <span className='text-ws-warning-fg'>{t('task.paste.conflict.title')}</span>
      )}
      icon={hasSet ? undefined : <AlertTriangle aria-hidden='true' className='text-ws-warning-fg' />}
      onClose={onCancel}
      maxWidth='max-w-lg'
      fullBleedOnPhone
    >
      <div className='flex flex-col gap-4'>
        {hasSet && (
          /*
           * PRESETS FIRST, the list behind a disclosure.
           *
           * A scenario set that cannot be found in the destination is a question with three
           * common answers and a long tail. Phone space is the constraint that decides the
           * shape: three buttons fit, a checklist of six rows plus a conflict list does not, and
           * the teacher who wants the common answer should not have to scroll past the rare one
           * to reach it.
           */
          <div className='flex flex-col gap-2'>
            <div className='flex flex-wrap gap-2'>
              {/* `Button`'s own `pressed` look, not a hand-rolled one — it already owns what an
                  on/off control looks like. Deliberately NOT a SegmentedControl: that is a
                  radiogroup, and ticking the list by hand lands on a selection no preset
                  describes, which a radio can only show by silently unsetting itself. */}
              <Button size='sm' pressed={include.size === 1} onClick={() => setInclude(new Set([subject.id]))}>
                {t('task.paste.bring.justThis')}
              </Button>
              {/* Only when the subject FOLLOWS: a leader paired with itself is not a choice. */}
              {subjectFollows && (
                <Button
                  size='sm'
                  pressed={include.size === 2 && include.has(leaderId)}
                  onClick={() => setInclude(new Set([subject.id, leaderId]))}
                >
                  {t('task.paste.bring.pair', { name: nameOf(items.find(i => i.block.id === leaderId)!.block, t) })}
                </Button>
              )}
              <Button
                size='sm'
                pressed={include.size === items.length}
                onClick={() => setInclude(new Set(items.map(i => i.block.id)))}
              >
                {t('task.paste.bring.all', { count: items.length })}
              </Button>
            </div>
            {/* ALWAYS OPEN. It is the only thing that says what is actually coming, and a gut
                check behind a disclosure is a gut check nobody takes. Measured at 375x667 with
                the conflicts open too: 665px of content in 667. */}
            <div className='rounded-xl border border-ws-border p-3'>
              <div className='flex flex-col'>
                {items.map(i => {
                  /* "Scenario" already means a VARIANT everywhere else — "Scenario 1 of 3",
                     "Add a scenario" — so it cannot also name the host block. "Leader" and "host"
                     appear in no teacher-facing string today, and a third word for a thing that
                     already has two is worse than either. `followedByLabel` is what the canvas
                     prints on this very block, so it is reused verbatim. */
                  const followers = items
                    .filter(x => (x.block.data as { followsBlockId?: string } | undefined)?.followsBlockId === leaderId)
                    .map(x => nameOf(x.block, t));
                  const label = i.block.id === leaderId && followers.length
                    ? `${nameOf(i.block, t)} — ${t('blocks.scenario.followedByLabel', { names: followers.join(', ') })}`
                    : nameOf(i.block, t);
                  /*
                   * The subject is a STATIC row, not a disabled checkbox. `CheckboxRow` forces a
                   * disabled toggle to read as OFF — right for a setting a mode took away, and
                   * exactly backwards here, where this is the one block that is certainly coming.
                   * It is not a choice, so it should not wear a control.
                   */
                  if (i.block.id === subject.id) {
                    return (
                      <div key={i.block.id} className='flex items-start gap-2.5 py-0.5'>
                        <Check aria-hidden='true' className='mt-0.5 h-5 w-5 shrink-0 text-ws-muted' strokeWidth={3} />
                        <span className='text-sm text-ws-text'>
                          {label}
                          <span className='sr-only'>{` ${t('task.paste.bring.always')}`}</span>
                        </span>
                      </div>
                    );
                  }
                  return (
                    <CheckboxRow
                      key={i.block.id}
                      checked={include.has(i.block.id)}
                      onToggle={() => toggleMember(i.block.id)}
                      label={label}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {conflicts.length > 0 && !hasSet && (
          <p className='text-sm text-ws-muted'>{t('task.paste.conflict.intro')}</p>
        )}

        {/* With a selection above, the conflicts go behind a count: a list that reflows under the
            teacher's thumb every time they tick a member is the specific thing to avoid. */}
        {hasSet && conflicts.length > 0 && (
          <Disclosure
            open={showConflicts}
            onToggle={() => setShowConflicts(v => !v)}
            /* Where the clash surfaces when a SET is being pasted — the heading above is about
               what to bring, so this row is the one that has to catch the eye. */
            tone='warning'
            label={t('task.paste.conflict.count', { count: conflicts.length })}
          >
            <div className='flex flex-col gap-4'>{conflicts.map(renderConflict)}</div>
          </Disclosure>
        )}

        {!hasSet && conflicts.map(renderConflict)}

        {/* Pinned below the scrolling list, so the way out is reachable however many rows there are. */}
        <div className='sticky bottom-0 flex justify-end gap-2 bg-ws-elevated pt-2'>
          <Button variant='secondary' onClick={onCancel}>{t('task.paste.conflict.cancel')}</Button>
          <Button variant='primary' onClick={() => onConfirm(choices, include)}>{t('task.paste.conflict.confirm')}</Button>
        </div>
      </div>
    </ModalShell>
  );
}
